/**
 * Bounded, read-only workbook diagnostics for the shared executor runtime.
 *
 * Both tools fail closed outside the FactSet Spreadsheet bridge, preserve
 * caller cancellation, and cap only the serialized result replayed to the
 * model. The host remains responsible for executing the underlying scan.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import {
  getCellErrors,
  getCircularReferences,
  shouldUseExcelIntentBridge,
} from "./excelHostOps";
import type {
  CellErrorEntry,
  WorksheetCellErrors,
} from "./services/factset/spreadsheetIntents";

/** Non-Excel markers scanned when the model does not provide an override. */
export const DEFAULT_FACTSET_ERROR_MARKERS: readonly string[] = ["#Calc"];

/** Maximum serialized diagnostic result size replayed to the model. */
export const DIAGNOSTIC_MAX_RESULT_CHARS = 256_000;
const DIAGNOSTIC_MAX_ERROR_RECORDS = 500;

/** Model-visible contract for circular-reference discovery. */
export const FIND_CIRCULAR_REFERENCES_TOOL: ChartCapabilityToolDef = {
  name: "find_circular_references",
  description:
    "Find circular references and report iterative-calculation settings for the target workbook.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      workbookName: {
        type: ["string", "null"],
        description: "Target workbook. Pass null to use the pinned workbook.",
      },
    },
    required: ["workbookName"],
    additionalProperties: false,
  },
};

/** Model-visible contract for workbook calculation-error discovery. */
export const FIND_CALC_ERRORS_TOOL: ChartCapabilityToolDef = {
  name: "find_calc_errors",
  description:
    "Find standard Excel errors and selected FactSet error markers in the target workbook.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      workbookName: {
        type: ["string", "null"],
        description: "Target workbook. Pass null to use the pinned workbook.",
      },
      factsetErrors: {
        type: ["array", "null"],
        items: { type: "string" },
        description: "Additional markers; null defaults to ['#Calc'].",
      },
    },
    required: ["workbookName", "factsetErrors"],
    additionalProperties: false,
  },
};

/** Read-only workbook diagnostic tools registered by the shared runtime. */
export const EXECUTOR_DIAGNOSTIC_TOOLS: readonly ChartCapabilityToolDef[] = [
  FIND_CIRCULAR_REFERENCES_TOOL,
  FIND_CALC_ERRORS_TOOL,
];

function resolveWorkbook(
  ctx: ExecutorToolRuntimeContext,
  workbookName: unknown
): string | undefined {
  return typeof workbookName === "string" && workbookName.trim()
    ? workbookName
    : ctx.session.pinnedWorkbookName;
}

function notSupported(workbookName: string | undefined): string {
  return JSON.stringify({
    ok: false,
    error: "not_supported",
    workbookName: workbookName ?? null,
  });
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AbortError"
  );
}

function serializeBoundedCellErrors(
  workbookName: string | null,
  factsetErrorsScanned: string[],
  success: boolean,
  results: WorksheetCellErrors[],
  hostError?: string
): string {
  const flattened: Array<{
    worksheetName: string;
    kind: "excelErrors" | "otherErrors";
    entry: CellErrorEntry;
  }> = [];
  for (const result of results) {
    for (const entry of result.excelErrors ?? []) {
      flattened.push({
        worksheetName: result.worksheetName,
        kind: "excelErrors",
        entry,
      });
    }
    for (const entry of result.otherErrors ?? []) {
      flattened.push({
        worksheetName: result.worksheetName,
        kind: "otherErrors",
        entry,
      });
    }
  }
  const totalErrorCount = flattened.length;
  const bounded: WorksheetCellErrors[] = [];
  let returnedErrorCount = 0;
  for (const item of flattened.slice(0, DIAGNOSTIC_MAX_ERROR_RECORDS)) {
    let sheet = bounded.find(
      (candidate) => candidate.worksheetName === item.worksheetName
    );
    if (!sheet) {
      sheet = { worksheetName: item.worksheetName };
      bounded.push(sheet);
    }
    const entries = sheet[item.kind] ?? [];
    sheet[item.kind] = [...entries, item.entry];
    const candidate = JSON.stringify({
      ok: success,
      workbookName,
      factsetErrorsScanned,
      results: bounded,
      totalErrorCount,
      returnedErrorCount: returnedErrorCount + 1,
      truncated: returnedErrorCount + 1 < totalErrorCount,
      ...(hostError ? { error: hostError } : {}),
    });
    if (candidate.length > DIAGNOSTIC_MAX_RESULT_CHARS) {
      sheet[item.kind] = entries.length > 0 ? entries : undefined;
      if (!sheet.excelErrors?.length && !sheet.otherErrors?.length) {
        bounded.pop();
      }
      break;
    }
    returnedErrorCount += 1;
  }
  return JSON.stringify({
    ok: success,
    workbookName,
    factsetErrorsScanned,
    results: bounded,
    totalErrorCount,
    returnedErrorCount,
    truncated: returnedErrorCount < totalErrorCount,
    ...(hostError ? { error: hostError } : {}),
  });
}

function serializeBoundedCircularReferences(
  workbookName: string | null,
  result: Awaited<ReturnType<typeof getCircularReferences>>
): string {
  const totalReferenceCount = result.worksheets.length;
  const worksheets = result.worksheets.slice(0, DIAGNOSTIC_MAX_ERROR_RECORDS);
  while (worksheets.length > 0) {
    const serialized = JSON.stringify({
      ok: true,
      workbookName,
      iterativeCalculation: result.iterativeCalculation,
      worksheets,
      totalReferenceCount,
      returnedReferenceCount: worksheets.length,
      truncated: worksheets.length < totalReferenceCount,
    });
    if (serialized.length <= DIAGNOSTIC_MAX_RESULT_CHARS) return serialized;
    worksheets.pop();
  }
  return JSON.stringify({
    ok: true,
    workbookName,
    iterativeCalculation: result.iterativeCalculation,
    worksheets: [],
    totalReferenceCount,
    returnedReferenceCount: 0,
    truncated: totalReferenceCount > 0,
  });
}

/**
 * Find circular references in the pinned or explicitly named workbook.
 *
 * @param ctx - Current executor runtime context.
 * @param rawInput - Strict tool input containing a nullable workbook name.
 * @param signal - Optional caller cancellation signal.
 * @returns A JSON diagnostic result.
 * @throws AbortError when the caller cancels the operation.
 */
export async function executeFindCircularReferencesTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal
): Promise<string> {
  const input = (rawInput ?? {}) as { workbookName?: string | null };
  const workbookName = resolveWorkbook(ctx, input.workbookName);
  if (!shouldUseExcelIntentBridge()) return notSupported(workbookName);
  try {
    const result = await getCircularReferences(workbookName, signal);
    return serializeBoundedCircularReferences(workbookName ?? null, result);
  } catch (error) {
    if (isAbortError(error)) throw error;
    return JSON.stringify({
      ok: false,
      workbookName: workbookName ?? null,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Find standard Excel and configured FactSet errors in a workbook.
 *
 * @param ctx - Current executor runtime context.
 * @param rawInput - Strict tool input with workbook and marker selections.
 * @param signal - Optional caller cancellation signal.
 * @returns A JSON diagnostic result.
 * @throws AbortError when the caller cancels the operation.
 */
export async function executeFindCalcErrorsTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal
): Promise<string> {
  const input = (rawInput ?? {}) as {
    workbookName?: string | null;
    factsetErrors?: string[] | null;
  };
  const workbookName = resolveWorkbook(ctx, input.workbookName);
  const factsetErrorsScanned = Array.isArray(input.factsetErrors)
    ? input.factsetErrors
    : Array.from(DEFAULT_FACTSET_ERROR_MARKERS);
  if (!shouldUseExcelIntentBridge()) return notSupported(workbookName);
  try {
    const result = await getCellErrors(
      workbookName,
      factsetErrorsScanned,
      signal
    );
    return serializeBoundedCellErrors(
      workbookName ?? null,
      factsetErrorsScanned,
      result.success,
      result.results,
      result.error
    );
  } catch (error) {
    if (isAbortError(error)) throw error;
    return JSON.stringify({
      ok: false,
      workbookName: workbookName ?? null,
      factsetErrorsScanned,
      results: [],
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
